import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from '@/App.vue'
import router from '@/router'
import { useAuthStore } from '@/stores/auth'
import '@/style.css'

/**
 * ТОЧКА ПОДКЛЮЧЕНИЯ 1 — MSW.
 *
 * Моки обязаны подняться ДО `app.mount()`, иначе первые запросы приложения уйдут
 * мимо перехватчика. Условие бута зафиксировано планом (§2.8) и не меняется:
 * только DEV и только при `VITE_USE_MOCK === 'true'`.
 */
async function startMocks(): Promise<void> {
  if (!import.meta.env.DEV || import.meta.env.VITE_USE_MOCK !== 'true') return

  const { worker } = await import('@/mocks/browser')
  await worker.start({ onUnhandledRequest: 'bypass' })
}

/**
 * Мост «транспорт ↔ стор авторизации» здесь НЕ собирается.
 *
 * Изначально он планировался в этом файле, но живёт в setup-е `stores/auth.ts`
 * (функция `wireBridge`), и это оказалось правильнее: направление зависимостей
 * то же самое (`stores → lib`, цикла нет), а стор гарантированно собирает мост
 * при первом же `useAuthStore()` — в том числе в тестах и в любой точке входа,
 * которая не проходит через этот файл.
 *
 * Правило слоёв `types ← lib ← api ← stores ← composables/views` (§2.3) при этом
 * соблюдается: `lib/http.ts` по-прежнему ничего не знает о сторах и получает
 * `setAuthLostHandler` / `setTokensRotatedHandler` инъекцией сверху.
 */

async function bootstrap(): Promise<void> {
  await startMocks()

  const app = createApp(App)

  app.use(createPinia())

  // Инстанцируем стор явно между пинией и роутером. Сам он поднялся бы и лениво
  // — на первом `useAuthStore()` внутри гварда, — но тогда момент сборки моста
  // с транспортом зависел бы от того, какой гвард сработал первым. Здесь порядок
  // виден: пиния есть, роутер ещё не начал первую навигацию.
  useAuthStore()

  app.use(router)

  app.mount('#app')
}

void bootstrap()
